import { ComponentKey } from 'enums/componentKey'
import * as Yup from 'yup'
import {
  DocumentProcessDialogConfig,
  DocumentProcessFormConfig,
  ExtractionResult
} from '../types'
import { BankDetailsExtractionResult } from 'api/documents'
import { requiredIbanSchema } from 'shared/utils/ibanSchema'
import { ribExtractionMapper } from './ribUploadConfig'

/**
 * Form values for attaching RIB to existing account (only IBAN/BIC for validation)
 */
export interface AttachRibFormValues extends Record<string, unknown> {
  iban: string
  bic: string
}

/**
 * Form configuration for attach RIB (simpler than create - no label needed)
 */
export const attachRibFormConfig: DocumentProcessFormConfig<AttachRibFormValues> = {
  fields: [
    {
      name: 'iban',
      componentKey: ComponentKey.Iban,
      label: 'IBAN',
      placeholder: 'FR76 3000 1007 9412 3456 7890 185',
      required: true,
      colSpan: 6
    },
    {
      name: 'bic',
      componentKey: ComponentKey.InputText,
      label: 'BIC / SWIFT',
      placeholder: 'BNPAFRPP',
      required: false,
      type: 'text',
      colSpan: 6
    }
  ],
  validationSchema: Yup.object().shape({
    iban: requiredIbanSchema,
    bic: Yup.string()
      .min(8, 'Le BIC doit contenir au moins 8 caractères')
      .max(11, 'Le BIC ne peut pas dépasser 11 caractères')
      .nullable()
  }) as Yup.ObjectSchema<AttachRibFormValues>,
  initialValues: {
    iban: '',
    bic: ''
  }
}

/**
 * Mapper to convert extraction result to attach form values
 */
export const attachRibExtractionMapper = (
  extracted: BankDetailsExtractionResult
): Partial<AttachRibFormValues> => ({
  iban: extracted.iban || '',
  bic: extracted.bic || ''
})

/**
 * Creates an attach RIB dialog configuration for existing bank accounts
 */
export const createAttachRibConfig = (options: {
  bankAccountId: string
  bankAccountIban: string
  onExtractIban: (storedFileId: string) => Promise<BankDetailsExtractionResult>
  onAttachRib: (values: AttachRibFormValues, storedFileId: string, bankAccountId: string) => Promise<{ success: boolean; errorMessage?: string }>
  onSuccess?: () => void
  onCancel?: () => void
}): DocumentProcessDialogConfig<AttachRibFormValues, BankDetailsExtractionResult> => ({
  title: 'Attacher un RIB',
  subtitle: `Uploadez le RIB pour le compte ${options.bankAccountIban}`,

  acceptedFormats: '.pdf,.jpg,.jpeg,.png',
  uploadLabel: 'Glissez-déposez votre RIB ici',
  maxFileSizeMB: 10,

  formConfig: {
    ...attachRibFormConfig,
    initialValues: {
      iban: options.bankAccountIban,
      bic: ''
    }
  },

  extraction: {
    onExtract: async (storedFileId: string): Promise<ExtractionResult<BankDetailsExtractionResult>> => {
      try {
        const result = await options.onExtractIban(storedFileId)
        return {
          success: result.isSuccess ?? false,
          data: result,
          errorMessage: result.errorMessage ?? undefined
        }
      } catch (error) {
        return {
          success: false,
          errorMessage: 'Erreur lors de l\'extraction de l\'IBAN'
        }
      }
    },
    mapper: ribExtractionMapper,
    loadingMessage: 'Extraction de l\'IBAN en cours...'
  },

  submit: {
    onSubmit: async (values: AttachRibFormValues, storedFileId: string) => {
      return options.onAttachRib(values, storedFileId, options.bankAccountId)
    },
    submitLabel: 'Attacher le RIB',
    successMessage: 'RIB attaché avec succès'
  },

  onSuccess: options.onSuccess,
  onCancel: options.onCancel
})
